// client/src/pages/admin/ManageOrders.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';

export default function ManageOrders() {
  const token = localStorage.getItem('authToken');
  const [orders, setOrders] = useState([]);

  // Track the order currently being edited
  const [editingOrderId, setEditingOrderId] = useState(null);

  // Fields we edit
  const [editPaymentStatus, setEditPaymentStatus] = useState('');
  const [editOrderStatus, setEditOrderStatus] = useState('');
  const [editShippingAddress, setEditShippingAddress] = useState('');
  const [editTracking, setEditTracking] = useState('');

  // Possible status values (dropdowns)
  const paymentStatusOptions = ['Pending', 'Paid', 'Refunded', 'Failed'];
  const orderStatusOptions = ['Pending', 'Processing', 'Shipped', 'Delivered', 'Cancelled'];

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line
  }, []);

  const fetchOrders = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/orders`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrders(data);
    } catch (err) {
      console.error('Error fetching orders:', err);
    }
  };

  // Begin editing a specific order => fill local state
  const handleEdit = (order) => {
    setEditingOrderId(order._id);
    setEditPaymentStatus(order.paymentStatus || '');
    setEditOrderStatus(order.orderStatus || '');
    setEditShippingAddress(order.shippingAddress || '');
    setEditTracking(order.trackingNumber || '');
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingOrderId(null);
    setEditPaymentStatus('');
    setEditOrderStatus('');
    setEditShippingAddress('');
    setEditTracking('');
  };

  // Save changes => PUT /api/admin/order/:orderId
  const handleSaveEdit = async (orderId) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/order/${orderId}`,
        {
          paymentStatus: editPaymentStatus,
          orderStatus: editOrderStatus,
          shippingAddress: editShippingAddress
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Order updated successfully (customer notified).');
      setEditingOrderId(null);
      fetchOrders();
    } catch (err) {
      console.error('Error updating order:', err);
      alert('Failed to update order');
    }
  };

  // Delete order => DELETE /api/admin/order/:orderId
  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/admin/order/${orderId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Order deleted');
      fetchOrders();
    } catch (err) {
      console.error('Error deleting order:', err);
      alert('Failed to delete order');
    }
  };

  // Update only the tracking number => PUT /api/admin/order/tracking
  const updateTracking = async (orderId, newTracking) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/order/tracking`,
        { orderId, trackingNumber: newTracking },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Tracking updated & customer notified via email.');
      setEditTracking('');
      fetchOrders();
    } catch (err) {
      console.error('Error updating tracking:', err);
      alert('Failed to update tracking');
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Manage Orders</h1>

      {orders.map(order => {
        const isEditing = (editingOrderId === order._id);

        return (
          <div key={order._id} className="border p-3 rounded mb-4">
            {isEditing ? (
              // === EDIT MODE ===
              <div>
                <p><strong>Order ID:</strong> {order._id}</p>
                <p><strong>User:</strong> {order.user?.name} ({order.user?.email})</p>
                <p><strong>Total:</strong> ${order.totalPrice}</p>

                {/* Payment Status Dropdown */}
                <div className="mt-2">
                  <label className="block font-medium">Payment Status:</label>
                  <select
                    className="border p-1"
                    value={editPaymentStatus}
                    onChange={(e) => setEditPaymentStatus(e.target.value)}
                  >
                    <option value="">Select...</option>
                    {paymentStatusOptions.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>

                {/* Order Status Dropdown */}
                <div className="mt-2">
                  <label className="block font-medium">Order Status:</label>
                  <select
                    className="border p-1"
                    value={editOrderStatus}
                    onChange={(e) => setEditOrderStatus(e.target.value)}
                  >
                    <option value="">Select...</option>
                    {orderStatusOptions.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>

                {/* Shipping Address */}
                <div className="mt-2">
                  <label className="block font-medium">Shipping Address:</label>
                  <input
                    type="text"
                    className="border p-1 w-full max-w-sm"
                    value={editShippingAddress}
                    onChange={(e) => setEditShippingAddress(e.target.value)}
                  />
                </div>

                {/* Tracking Number (only visible in edit mode) */}
                <div className="mt-2">
                  <label className="block font-medium">Tracking #:</label>
                  <input
                    type="text"
                    className="border p-1 w-full max-w-sm"
                    value={editTracking}
                    onChange={(e) => setEditTracking(e.target.value)}
                  />
                  <button
                    onClick={() => updateTracking(order._id, editTracking)}
                    className="ml-2 bg-primaryGreen text-white px-3 py-1 rounded hover:bg-green-500"
                  >
                    Update Tracking
                  </button>
                </div>

                <div className="mt-2 flex space-x-2">
                  <button
                    onClick={() => handleSaveEdit(order._id)}
                    className="bg-primaryGreen text-white px-3 py-1 rounded hover:bg-green-500"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="bg-gray-400 text-white px-3 py-1 rounded hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              // === VIEW MODE ===
              <div>
                <p><strong>Order ID:</strong> {order._id}</p>
                <p><strong>User:</strong> {order.user?.name} ({order.user?.email})</p>
                <p><strong>Total:</strong> ${order.totalPrice}</p>
                <p><strong>Payment Status:</strong> {order.paymentStatus}</p>
                <p><strong>Order Status:</strong> {order.orderStatus || 'N/A'}</p>
                <p><strong>Tracking #:</strong> {order.trackingNumber || 'N/A'}</p>
                <p><strong>Shipping Address:</strong> {order.shippingAddress}</p>

                <div className="mt-2">
                  <strong>Items:</strong>
                  <ul className="list-disc ml-6">
                    {order.orderItems.map(item => (
                      <li key={item._id} className="text-sm">
                        {item.product?.name} x {item.quantity} = ${item.price}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-2 flex space-x-2">
                  <button
                    onClick={() => handleEdit(order)}
                    className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteOrder(order._id)}
                    className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
